import { EventCacheData } from 'hooks/useLRQ/useEventCache';
import {
  DashboardType,
  DashboardV2,
  FacetFn,
  Plot,
  PlotQueryInput,
  Dashboard as RawDashboard,
  SliceInfo,
} from 'common/graphqlTypes';
import { adjustBucketAtMinMax, getFunction, isFacetGraph } from 'common/util/graph';
import { escapeFacetName, quote } from 'common/util/string';
import { getTimeDifferenceInMs, parseDuration } from 'common/util/time';
import { addTermToFilter, extendFilter, simplifyFilter } from 'common/util/filter/filter';
import { scaleNumSlices } from 'components/search/SearchGraphsPage/GraphStateManager';
import { isNumber } from 'lodash';

import * as K from 'common/constants';
import { EVENT_MODE, URL_PARAM } from 'common/constants';
import { FacetsCompletionInfo, UrlParams } from 'common/types';
import {
  Dashboard,
  DashboardConfigType,
  DashboardJSONType,
  DashboardLayoutType,
  DashboardParam,
  FilterConfigType,
  GraphConfigType,
  GraphPlotConfigType,
  ParameterConfigType,
} from 'components/dashboards/types';
import { buildUrl } from 'common/functions';

// For consistency, a few defaults
export const DEFAULT_GRAPH_STYLE = 'line';
export const DEFAULT_INTERVAL = 'auto';
export const DEFAULT_INTERVAL_VALUE = '10 minutes';

// The legacy default number of data points for dashboard line charts (that we'll scale up or down)
export const LINE_CHART_SLICES = 500;
export const DEFAULT_SMOOTHED_SLICES = LINE_CHART_SLICES / 2;

// The legacy default number of bars dashboard bar charts (that the user can override)
export const DEFAULT_BAR_CHART_SLICES = 24;
export const MAX_BAR_CHART_SLICES = 200;

export const isLayoutFixed = (dashboardConfig: DashboardConfigType): boolean => !!dashboardConfig.options.layout.fixed;
export const isLayoutLocked = (dashboardConfig: DashboardConfigType): boolean =>
  !!dashboardConfig.options.layout.locked;
export const isLayoutEditable = (dashboardConfig: DashboardConfigType): boolean =>
  !!dashboardConfig.options.layout.fixed && !!dashboardConfig.options.layout.locked;
export const hasLayoutOptions = (dashboardConfig: DashboardConfigType): boolean =>
  !!Object.keys(dashboardConfig.options.layout);

export type DASH_VIZ_TYPE = {
  graphStyle: string; // as given, or if undefined and not a PQ or report => DEFAULT_GRAPH_STYLE
  isPowerQuery: boolean;
  isReport: boolean;
  isTimeseries: boolean; // x-axis is time, so line, area and bar
  isChart: boolean; // is a Highchart chart, so a timeseries or pie
  isBar: boolean;
  isArea: boolean;
  isLine: boolean;
  isPie: boolean; // pie or donut
  isMarkdown: boolean;
  isNumber: boolean;
  isBreakdown: boolean;
  isSmoothed: boolean;
  isDistribution: boolean;
  isComparison: boolean;
  isHoneyComb: boolean;
};

export const getType = (graphConfig: GraphConfigType): DASH_VIZ_TYPE => {
  const types: DashboardJSONType = {};
  types.isPowerQuery = !!graphConfig.query && !(graphConfig.graphStyle === K.PANEL_TYPE.HONEYCOMB);
  types.isReport = !!graphConfig.columns || !!graphConfig.keys;
  types.graphStyle = graphConfig.graphStyle;
  if (!types.isPowerQuery && !types.isReport && !graphConfig.graphStyle) {
    types.graphStyle = DEFAULT_GRAPH_STYLE;
  }
  types.isTimeseries = ['line', 'stacked_bar', 'stacked', 'area', 'bar'].includes(types.graphStyle);
  types.isBar = types.isTimeseries && (types.graphStyle === 'stacked_bar' || types.graphStyle === 'bar');
  types.isArea = types.isTimeseries && (types.graphStyle === 'stacked' || types.graphStyle === 'area');
  types.isLine = types.isTimeseries && types.graphStyle === DEFAULT_GRAPH_STYLE;
  types.isPie = types.graphStyle === 'donut' || types.graphStyle === 'pie';
  types.isChart = types.isTimeseries || types.isPie;
  types.isMarkdown = types.graphStyle === 'markdown';
  types.isNumber = types.graphStyle === 'number';
  types.isBreakdown = !!graphConfig.breakdownFacet;
  types.isDistribution = types.graphStyle === 'distribution';
  types.isSmoothed = graphConfig.lineSmoothing === 'smoothCurves';
  types.isHoneyComb = graphConfig.graphStyle === K.PANEL_TYPE.HONEYCOMB;
  return types as DASH_VIZ_TYPE;
};

const FULL_WIDTH = 60;
const THIRD_WIDTH = FULL_WIDTH / 3;

export const DX_ROW = FULL_WIDTH;
export const MIN_DX = 6;
export const DY_ROW = 4;

const MIN_DY = 3;

const DX_PIE = THIRD_WIDTH;
const DY_PIE = 14;
const DX_NUMBER = 12;
const DY_NUMBER = 5;
const DX_TIMESERIES = THIRD_WIDTH;
const DY_TIMESERIES = 14;

/**
 * Determine the default dimensions of a graph based on it's graphStyle.
 *
 * @param graphConfig A graph config to process
 */
const getDefaultDimensions = (graphConfig: GraphConfigType): { dx: number; dy: number } => {
  const vizType = getType(graphConfig);
  if (vizType.isNumber) {
    return { dx: DX_NUMBER, dy: DY_NUMBER };
  }

  if (vizType.isPie) {
    return { dx: DX_PIE, dy: DY_PIE };
  }

  return { dx: DX_TIMESERIES, dy: DY_TIMESERIES };
};

/**
 * Calculate any missing layouts for graphs on a dashboard.
 *
 * @param graphs An array of graphs to process
 */
export function calcLayout(graphs: GraphConfigType[]): ReactGridLayout.Layout[] {
  let x = 0;
  let widthOverride: number | null = null;
  const layouts: ReactGridLayout.Layout[] = [];
  // When the layout is not provided -
  // if there is only one chart on the dashboard then make it full width
  // if there are two charts, then make them half width each
  // three onwards we go to our default size of third width
  const hasLayoutBeenSpecified = graphs.some((config) => {
    const graph = config as DashboardJSONType;
    return !!graph.layout;
  });

  if (!hasLayoutBeenSpecified && graphs.length > 0 && graphs.length <= 2) {
    widthOverride = FULL_WIDTH / graphs.length;
  }

  graphs.forEach((config, index: number) => {
    const graph = config as DashboardJSONType;
    const item = { ...graph };
    let layout = item.layout;

    if (!layout) {
      const dim = getDefaultDimensions(config);
      layout = {
        x,
        y: 999999,
        w: widthOverride ?? dim.dx,
        h: dim.dy,
      };
    }

    if (layout.w < MIN_DX) {
      layout.w = MIN_DX;
    }
    if (layout.h < MIN_DY) {
      layout.h = MIN_DY;
    }

    layout.minW = MIN_DX;
    layout.minH = MIN_DY;
    layout.i = index.toString();
    layouts.push(layout);

    x = layout.x + layout.w;
    if (x >= DX_ROW) {
      x = 0;
    }
  });

  return layouts;
}

/**
 * Replace the layout in graph config with a fixed width one
 *
 * @param graphs       An array of Scalyr configs for graphs (each of which can either be a chart or a table)
 * @param numColumns   A number of columns to use
 * @param keepExisting true to only reset the layout of graphs with no existing one
 */
export const setColumnLayouts = (
  graphs: GraphConfigType[],
  numColumns: number,
  keepExisting = false,
): GraphConfigType[] => {
  const newGraphs: GraphConfigType[] = graphs.map((graph) => {
    const newGraph: GraphConfigType = { ...graph };
    if (!keepExisting) {
      delete newGraph.layout;
    }
    return newGraph;
  });

  let x = 0;
  const dx = Math.floor(DX_ROW / numColumns);
  const dy = Math.floor(DY_TIMESERIES * (3 / numColumns));
  newGraphs.forEach((config) => {
    const layout =
      keepExisting && config.layout
        ? config.layout
        : {
            x,
            y: 0,
            w: dx,
            h: dy,
          };
    x += layout.w;
    if (x >= DX_ROW) {
      x = 0;
    }
    config.layout = layout;
  });

  return newGraphs;
};

/**
 * Calculate the data slicing for this graph
 *
 * @param graphConfig The configuration file for this graph
 */
export const calcGraphSlicing = (
  startTime: string,
  endTime: string,
  dashboard: Dashboard,
  graphIndex: number,
): PlotQueryInput => {
  const graphConfig = dashboard.config.graphs[graphIndex] as GraphConfigType;
  const timeSpanMs = getTimeDifferenceInMs({ startTime, endTime }, null);
  const vizType = getType(graphConfig);

  // For these, we only ever ask for one slice
  if (vizType.isNumber || vizType.isPie) {
    return {
      autoAlign: false,
      slices: 1,
    } as unknown as PlotQueryInput;
  }

  // For bar charts, the configured (or default) barWidth [interval] is always used
  if (vizType.isBar) {
    // Using default bar chart settings
    if (!graphConfig.barWidth || graphConfig.barWidth.toLowerCase() === DEFAULT_INTERVAL) {
      return {
        // if numBars is not present, let the api autoAlign and not worry about returning the exact number of slices.
        autoAlign: !graphConfig.numBars,
        slices: graphConfig.numBars
          ? Math.min(Number(graphConfig.numBars), MAX_BAR_CHART_SLICES)
          : DEFAULT_BAR_CHART_SLICES,
      } as unknown as PlotQueryInput;
    }

    // If the configured interval is greater than the timespan, we just show one bar
    const intervalMs = parseDuration(graphConfig.barWidth || DEFAULT_INTERVAL_VALUE);
    if (intervalMs >= timeSpanMs) {
      return {
        autoAlign: false,
        slices: 1,
      } as unknown as PlotQueryInput;
    }

    // Otherwise, honor the given configuration
    return {
      autoAlign: true,
      sliceWidth: graphConfig.barWidth || DEFAULT_INTERVAL_VALUE,
    } as unknown as PlotQueryInput;
  }

  // For smoothed line/area charts, we use a fixed number of slices
  if (vizType.isSmoothed) {
    return {
      autoAlign: false,
      slices: DEFAULT_SMOOTHED_SLICES,
    } as unknown as PlotQueryInput;
  }

  // For line charts that have a time interval included, we adjust the slices for that interval
  if (graphConfig.barWidth) {
    const slices = scaleNumSlices(
      startTime,
      endTime,
      false,
      LINE_CHART_SLICES,
      graphConfig.barWidth,
      graphConfig.graphStyle,
    );
    return {
      autoAlign: false,
      slices,
    } as unknown as PlotQueryInput;
  }

  // For non-smoothed ones, we try to calculate a good number of points to use
  // based on the width of the graph on the page. If it's too jagged, look at
  // adjusting LINE_CHART_SLICES.
  const layout = (dashboard.layouts as ReactGridLayout.Layout[])[graphIndex];
  const chartGridWidth = layout.w / DX_ROW;
  const slices: number = Math.round(chartGridWidth * LINE_CHART_SLICES);

  return {
    autoAlign: false,
    slices,
  } as unknown as PlotQueryInput;
};

/**
 * Convert "params" url param from stringified JSON to a map of key value pairs.
 *
 * @param params Stringified JSON
 */
export const parseUrlParamValues = (params: string): Map<string, string> => {
  if (params) {
    const searchParams = new URLSearchParams(params);
    const map = new Map<string, string>();
    searchParams.forEach((value, key) => map.set(key, value));
    return map;
  }
  return new Map<string, string>();
};

/**
 * Generate a list of all dashboard parameters with their display and substitution values.
 *
 * @param paramConfigs An array of resolved ParameterConfigType values - for possible default values
 * @param urlParams    A map of any parameter url values - for their current values
 */
export const generateParamsList = (
  paramConfigs: ParameterConfigType[],
  urlParams: string | null,
): Array<DashboardParam> => {
  // Collect any default values that are specified in the config (or if there's a list, the first item).
  // Note that there's the name of the parameter, it's current value, and a possible shorthand alias
  // that appears as the key name for the config value. The alias is used to shorten what appears in the
  // dropdown.
  const defaultValues = new Map<string, DashboardParam>();
  paramConfigs.forEach((paramConfig) => {
    let value = '';
    let display = '';
    const name = paramConfig.name;
    if (paramConfig.defaultValue) {
      display = paramConfig.defaultValue;
      value = paramConfig.defaultValue;
    } else if (paramConfig.labelsAndValues?.length) {
      display = Object.keys(paramConfig.labelsAndValues[0])[0];
      value = Object.values(paramConfig.labelsAndValues[0])[0];
    }
    return defaultValues.set(paramConfig.name, {
      name,
      value,
      display,
      defaultValue: value,
      config: paramConfig,
    });
  });

  // override the default values with any current query string values
  const paramValues = [] as DashboardParam[];
  const urlValues = parseUrlParamValues(urlParams || '');
  defaultValues.forEach((param: DashboardParam, name: string) => {
    const currentValue = urlValues.get(name);
    if (currentValue) {
      param.value = currentValue;
    }
    paramValues.push(param);
  });

  return paramValues;
};

export const generateFilterList = (
  dashboardFilters: FilterConfigType[],
  filterUrlParams: string | null,
): FilterConfigType[] => {
  const defaultValues = new Map<string, any>();
  dashboardFilters.forEach((filterConfig) => {
    let value = '';
    let display = '';
    const name = filterConfig.name;
    if (filterConfig.defaultValue) {
      display = filterConfig.defaultValue;
      value = filterConfig.defaultValue;
    } else if (filterConfig.labelsAndValues?.length) {
      display = Object.keys(filterConfig.labelsAndValues[0])[0];
      value = Object.values(filterConfig.labelsAndValues[0])[0];
    }
    return defaultValues.set(filterConfig.name, {
      name,
      value,
      display,
      defaultValue: value,
    });
  });

  const paramValues = [] as FilterConfigType[];
  const urlValues = parseUrlParamValues(filterUrlParams || '');
  defaultValues.forEach((filterConfig: any, name: string) => {
    const currentValue = urlValues.get(name);
    if (currentValue) {
      filterConfig.value = currentValue;
    }
    paramValues.push(filterConfig);
  });

  return paramValues;
};

/**
 * Prepare the backend dashboard page data for display.
 *
 * @param rawDashboard       The unprocessed data we got from the backend {@link getDashboard} query
 * @param urlParamValues     The url param values.
 * @param dashboardFilterUrlParams     The "dashboardFilters" url param values
 * @param activeTab           The active tab name of the dashboard - "default" if not tabs are present
 */
export const processConfig = (
  rawDashboard: (DashboardV2 & RawDashboard) | null,
  urlParamValues: string | null,
  dashboardFilterUrlParams: string | null,
  activeTab?: string,
): Dashboard | null => {
  // Return null if the dashboard isn't ready
  if (!rawDashboard) {
    return null;
  }

  // Convert the top-level raw dashboard json config to typed data
  const name = rawDashboard.name ? rawDashboard.name : 'Untitled';
  const description = rawDashboard.description ?? '';
  const duration = rawDashboard.duration ?? '';

  let newRawDashboardTab: any;
  // TBD this check is to support both getDashboard and getDashboardV2 data, remove once V2 becomes a stable version
  if (rawDashboard.configType) {
    if (rawDashboard.configType === DashboardType.TABBED) {
      newRawDashboardTab = activeTab
        ? rawDashboard.tabs.find((tab: any) => tab.tabName === activeTab)
        : rawDashboard.tabs[0];
    } else if (rawDashboard.configType === DashboardType.NOT_SPECIFIED) {
      newRawDashboardTab = rawDashboard.tabs[0];
    }
  } else {
    newRawDashboardTab = rawDashboard;
  }
  const tabName = newRawDashboardTab.tabName ? newRawDashboardTab.tabName : '';
  const parameters = newRawDashboardTab.parameters ? JSON.parse(newRawDashboardTab.parameters) : [];
  const filters = newRawDashboardTab.filters ? JSON.parse(newRawDashboardTab.filters) : [];
  let graphs = newRawDashboardTab.graphs ? JSON.parse(newRawDashboardTab.graphs) : [];

  // The options stanza is nested, so make sure its structure is in place
  const optionsBasis = { layout: {} };
  const options = newRawDashboardTab.options
    ? { ...optionsBasis, ...JSON.parse(newRawDashboardTab.options) }
    : optionsBasis;

  if (options.layout && options.layout.columns) {
    graphs = setColumnLayouts(graphs as GraphConfigType[], Number(options.layout.columns), true);
  }

  const config = {
    name,
    description,
    options,
    duration,
    parameters,
    filters,
    graphs,
    isBuiltIn: rawDashboard.isBuiltIn, // these can't be deleted
    tabName,
  };

  // It's an empty dashboard, so bail out
  if (!newRawDashboardTab.graphs || newRawDashboardTab.graphs.length === 0) {
    return { config, layouts: [], params: [], filters: [] };
  }

  return {
    config,
    layouts: calcLayout(graphs),
    params: generateParamsList(config.parameters as ParameterConfigType[], urlParamValues),
    filters: generateFilterList(config.filters, dashboardFilterUrlParams),
  };
};

export const trimUnusedOptions = (options: DashboardJSONType): DashboardJSONType => {
  if (options.layout.locked !== undefined && options.layout.locked === 0) {
    delete options.layout.locked;
  }
  if (!Object.keys(options.layout).length) {
    delete options.layout;
  }
  return options;
};
/**
 * Return just the part of a single layout that we want to persist.
 *
 * @param layout A react-grid-layout's layout configs, with extra data we don't want
 */
export const trimLayout = (layout: ReactGridLayout.Layout): DashboardLayoutType => ({
  x: layout.x,
  y: layout.y,
  w: layout.w,
  h: layout.h,
});

/**
 * Return just the parts of each layout that we want to persist.
 *
 * @param layouts An array of the react-grid-layout's layout configs, with extra data we don't want
 */
export const trimLayouts = (layouts: ReactGridLayout.Layout[]): DashboardLayoutType[] => {
  const justLayouts: DashboardLayoutType[] = layouts.map((layout) => trimLayout(layout as ReactGridLayout.Layout));
  return justLayouts;
};

/**
 * Insert the react-grid-layout data into each item in the dashboard
 *
 * @param graphs  An array of Scalyr configs for graphs (each of which can either be a chart or a table)
 * @param layouts An array of the react-grid-layout's layout configs
 */
export const getGraphsWithLayouts = (
  graphs: GraphConfigType[],
  layouts: ReactGridLayout.Layout[],
): GraphConfigType[] => {
  const trimmedLayouts = trimLayouts(layouts);
  const newGraphs: GraphConfigType[] = graphs.map((graph, index) => {
    const newGraph: GraphConfigType = { ...graph };
    newGraph.layout = trimmedLayouts[index];
    delete newGraph.colSpan; // These two values are removed because they are leftover from a
    delete newGraph.rowSpan; // previous failed attempt at direct layout.
    return newGraph;
  });

  return newGraphs;
};

/**
 * Append quotes around param value for dashboard parameters appropriately. Typically used for
 * dashboard facet parameters.
 *
 * @param param   The param value
 */
export const quoteParamValue = (param: string): string => {
  if (param === '*' || param.toLowerCase() === 'true' || param.toLowerCase() === 'false' || !isNaN(parseInt(param))) {
    return param;
  }

  return quote(param, false, true);
};

/**
 * For each substring of the form #xxx# in input, if xxx appears as a key in the parameters list,
 * replace the substring with the parameter value. For instance, given the parameter table:
 *   x -> ABC
 *   foo -> BAR
 *
 * And the following input string:
 *   one#two#x#three#foo##
 *
 * we return:
 *   one#twoABCthreeBAR#
 *
 * @param parameters A map of pairs with parameter names and values to replace
 * @param input      A string to replace tokens in
 */
export const substituteParameters = (parameters: Array<DashboardParam>, input: string | undefined): string => {
  if (!input) {
    return '';
  }

  let inputValue = input as string;
  parameters.forEach((param) => {
    const paramValue = param.config.facet ? quoteParamValue(param.value) : param.value;
    inputValue = inputValue.split(`#${param.name}#`).join(paramValue);
  });

  return inputValue;
};

/**
 * Check if this is the right key to use when extracting any returned breakdown plot data.
 *
 * Breakdown charts will return two sets of values: one for the values and one for the counts, so we need to use the
 * right one. Sometimes there isn't even a key to check -- it's really inconsistent -- so it needs to be forgiving.
 *
 * @param facetFn     The FacetFn structure that's returned with the plot data
 * @param plotConfig  The original plot configuration
 * @param defaultFn   The default function used if none was specified in the request
 * @param isBreakdown true if this is a breakdown, false if not
 */
const matchFn = (
  facetFn: FacetFn | undefined,
  plotConfig: GraphPlotConfigType,
  defaultFn: string,
  isBreakdown: boolean,
): boolean => {
  // If there's no facet function structure at all, that usually means a complex expression was used
  if (!isBreakdown || !facetFn) return true;

  // Sometimes there's no structure to the returned FacetFn, just a string, so we check for that. Also, the backend
  // will return 'sumPerSec', 'countPerSec', etc., even if the requested function was '...PerSecond', not '...PerSec'.
  const dataFn = (facetFn.operator ? facetFn.operator : facetFn.toString() || '').replace('PerSecond', 'PerSec');
  const configFn = (getFunction(plotConfig.filter) || getFunction(plotConfig.facet) || defaultFn).replace(
    'PerSecond',
    'PerSec',
  );

  return dataFn === configFn;
};

export type DashboardPlotLabelValue = {
  name: string;
  y: number;
  percent: string;
  sliced?: boolean;
  selected?: boolean;
  color?: string;
  visible?: boolean;
};
export type DashboardPlotValuePair = [number, number];

export type DashboardPlotData = {
  label?: string;
  color?: string;
  facet?: string;
  filter?: string;
  query?: string;
  plotStyle?: string;
  lineSmoothing?: string;
  smoothingInterval?: string;
  barWidth?: string;
  breakdownFacet?: string;
  breakdownLabel?: string;
  yScale?: string;
  maxValue?: number;
  isBar?: boolean;
  isPQ?: boolean;
  seriesData?: Array<DashboardPlotValuePair> | Array<DashboardPlotLabelValue>;
  options?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export const MAX_PLOTS = 20;

export const getPlotSettings = (
  graphConfig: GraphConfigType,
  plotConfig: GraphPlotConfigType,
  params: DashboardParam[],
): DashboardPlotData => {
  const vizType = getType(graphConfig);
  const facet = substituteParameters(params, plotConfig.facet || graphConfig.facet || '');
  const filter = substituteParameters(params, plotConfig.filter || graphConfig.filter || '');
  const label = substituteParameters(params, plotConfig.label || plotConfig.filter || plotConfig.facet || 'Untitled');
  const plotEntry: DashboardPlotData = {
    label,
    facet,
    filter,
    plotStyle: (plotConfig.plotStyle ? plotConfig.plotStyle : graphConfig.graphStyle) || DEFAULT_GRAPH_STYLE,
    lineSmoothing: graphConfig.lineSmoothing,
    isBar: vizType.isBar,
    isPQ: vizType.isPowerQuery,
    barWidth: graphConfig.barWidth,
  };
  return plotEntry;
};

export type MappedSeries = { seriesData: Array<[number, number]>; maxValue: number };

/**
 * Returns an MappedSeries object with xAxis and yAxis values mapped to array of pairs. Takes into acount sliceInfo for bar alignment.
 *
 * @param xAxis       xAxis timestamp values
 * @param yAxis       yAxis plot values
 * @param isBar       true if the series is a bar/column
 * @param sliceInfo   Metadata used to properly align bars with time axis
 */
export const getSeriesData = (
  xAxis: Array<number>,
  yAxis: Array<number>,
  isBar: boolean,
  sliceInfo: SliceInfo,
): MappedSeries => {
  const seriesData: Array<[number, number]> = [];
  const xValues = [...xAxis];
  let maxValue = Number.MIN_SAFE_INTEGER;

  // Account for partial bars at the start and end of the timerange
  if (isBar && sliceInfo && xValues.length > 0) {
    const [min, max] = adjustBucketAtMinMax(xValues[0], xValues[xValues.length - 1], sliceInfo);
    xValues[0] = min;
    xValues[xValues.length - 1] = max;
  }
  // Combine the data into time-value pairs and calculate some stats for later
  xValues.forEach((xValue, xIndex) => {
    const plotValue = yAxis[xIndex];
    if (plotValue > maxValue) {
      maxValue = plotValue;
    }
    seriesData.push([xValue, plotValue]);
  });

  return { seriesData, maxValue };
};

/**
 * Normalize the LRQ plot data into a simple array of plots with enough data to:
 *   1) Display the plot
 *   2) Display the legend
 *   3) Calculate the right parameters for pivoting to the search page
 *
 * @param graphConfig       The unadulterated config for this graph
 * @param results           The data return value from useEventCache
 * @param plotColors        The list of default plot colors
 * @param params            The current values for the dashboard parameters
 * @param includePlotValues true to generate and return the array of xAxis/yAxis data points, false to skip that
 */
export const getPlots = (
  graphConfig: GraphConfigType,
  results: EventCacheData[],
  plotColors: string[],
  params: DashboardParam[],
  includePlotValues?: boolean,
): Array<DashboardPlotData> => {
  const plotList = new Array<DashboardPlotData>();

  let plotConfigs = graphConfig.plots
    ? graphConfig.plots
    : [{ facet: graphConfig.facet || '', filter: graphConfig.filter || '' }];

  // Compare graphs have additional plots that are not defined in their config. We create one of them here so that we can access graphql request data further down.
  if (graphConfig.shift) {
    plotConfigs = plotConfigs.concat({ ...plotConfigs[0], label: `shifted(${plotConfigs[0].label})` });
  }

  if (plotConfigs) {
    const vizType = getType(graphConfig);
    const isBreakdown = vizType.isBreakdown || (vizType.isPowerQuery && vizType.isTimeseries);
    let plotIndex = 0;
    results.forEach((result, resultIndex) => {
      const plotConfig = plotConfigs[resultIndex];
      const graphData = result.plotData;
      if (graphData && plotConfig) {
        const plots: Plot[] = graphData.plots;
        if (plots) {
          for (let index = 0; index < plots.length && index < MAX_PLOTS; index += 1) {
            const plotSettings = getPlotSettings(graphConfig, plotConfig, params);
            const plotData = plots[index];
            const isFacet = isFacetGraph(plotSettings.facet);
            const defaultCountFn = plotSettings.isBar ? 'count' : 'countPerSec';
            const defaultFn = isFacet ? 'mean' : defaultCountFn;
            let seriesData: Array<[number, number]> = [];
            const sliceInfo = graphData.sliceInfo;
            let maxValue = Number.MIN_SAFE_INTEGER;

            if (matchFn(plotData.fn, plotConfig, defaultFn, isBreakdown)) {
              if (includePlotValues && plotData.samples?.length) {
                const series = getSeriesData(graphData.xAxis, plotData.samples, !!plotSettings.isBar, sliceInfo);
                seriesData = series.seriesData;
                maxValue = Math.max(series.maxValue, maxValue);
              }
              const label = isBreakdown ? plotData.label : plotSettings.label;
              const plotEntry: DashboardPlotData = {
                ...plotSettings,
                label,
                color: isBreakdown
                  ? plotColors[plotIndex % plotColors.length]
                  : plotConfig.color || plotColors[plotIndex % plotColors.length],
                plotStyle:
                  (plotConfig.plotStyle ? plotConfig.plotStyle : graphConfig.graphStyle) || DEFAULT_GRAPH_STYLE,
                breakdownFacet: graphConfig.breakdownFacet,
                breakdownLabel: isBreakdown ? `Break Down by ${graphConfig.breakdownFacet as string}` : '',
                lineSmoothing: graphConfig.lineSmoothing,
                isPQ: vizType.isPowerQuery,
                maxValue,
                barWidth: graphConfig.barWidth,
                seriesData,
              };
              plotIndex += 1;
              plotList.push(plotEntry);
            }
          }
        }
      }
    });
  }

  return plotList;
};

/**
 * Make sure a graph index references a valid config stanza.
 *
 * @param dashboardConfig  The dashboard config to check
 * @param graphIndex       The index of the graph within the dashboard config
 */
export const isValidGraphIndex = (dashboardConfig: DashboardConfigType, graphIndex: number): boolean =>
  !(
    Number.isNaN(graphIndex) ||
    graphIndex < 0 ||
    !dashboardConfig.graphs ||
    graphIndex >= dashboardConfig.graphs.length
  );

/**
 * Get the graph title from a graph config, returning 'Untitled' if not found.
 *
 * @param graphConfig  The graph config to use
 */
export const getGraphTitle = (graphConfig: GraphConfigType): string =>
  graphConfig.label || graphConfig.title || 'Untitled';

type mutationVariablesType = {
  [key: string]: string | number;
};
/**
 * Create updated dashboard graph config for mutation
 *
 * @param dashboardName The name of the dashboard to mutate
 * @param graphConfig   The graph config to use
 * @param graphIndex    The index of the graph within the dashboard config
 * @param plots         A list of plots to be updated
 */
export const createUpdatedGraphVariables = (
  dashboardName: string,
  graphConfig: GraphConfigType,
  graphIndex: number | undefined,
  plots: GraphPlotConfigType[],
  activeDashboardTab?: string,
): mutationVariablesType => {
  const graphJson = { ...graphConfig } as unknown as DashboardJSONType;
  graphJson.plots = plots;

  // legacy requires a plots array even though it's a breakdown
  if (graphJson.breakdownFacet) {
    if (!plots.length) {
      const emptyRatePlot = { facet: 'rate', filter: '' };
      plots.push(emptyRatePlot);
    }

    graphJson.plots = graphJson.plots.map((plot: GraphPlotConfigType) => ({
      ...plot,
      filter: graphJson.filter || plot.filter || '',
    }));
  }

  if (graphJson.layout) {
    // layout has extra fields that need to be removed before persisting
    graphJson.layout = trimLayout(graphJson.layout as ReactGridLayout.Layout);
  }
  if (graphJson.graphStyle !== K.CHART_TYPES.STACKED_BAR) {
    // legacy drops the barWidth if graphStyle is not stacked_bar
    delete graphJson.barWidth;
  }

  // Cleanup leftover options not being used
  if (graphJson.graphStyle !== K.CHART_TYPES.STACKED_BAR) {
    delete graphJson.barWidth;
    delete graphJson.numBars;
  }
  delete graphJson.intervalType;
  delete graphJson.smoothingInterval;
  if (graphJson.breakdownFacet !== undefined && graphJson.breakdownFacet.length === 0) {
    delete graphJson.breakdownFacet;
  }
  const tabName = activeDashboardTab ? activeDashboardTab : '';
  return graphIndex !== undefined
    ? {
        dashboardName,
        graphIndex,
        graph: JSON.stringify(graphJson),
        tabName,
      }
    : {
        dashboardName,
        graph: JSON.stringify(graphJson),
        tabName,
      };
};

export const createDistributionURL = (facet: string, teamEmails?: string): string => {
  const searchParams: Record<string, string> = {
    [URL_PARAM.MODE]: EVENT_MODE.DISTRIBUTION,
    [URL_PARAM.FACET]: facet,
  };

  if (teamEmails) {
    searchParams.teamEmails = teamEmails;
  }

  return `${K.ROUTE.EVENTS}?${new URLSearchParams(searchParams)}`;
};

/**
 * Create a link to the event page from a normalized plot list.
 *
 * @param startTime          graph start time
 * @param endTime            graph end time
 * @param graphConfig        config for graph that contains the plots
 * @param plots              array of normalized plots as returned from getPlots (above)
 * @param index              plot index
 * @param isGraphPage        true if URL should be for the Search Graph page
 * @param isBreakdownPlot    true if the plot has a breakdown facet
 * @param prefilter           a prefilter string for dashboards
 */
export const createEventsPageUrl = (
  startTime: string,
  endTime: string,
  graphConfig: GraphConfigType,
  plots: Array<DashboardPlotData> | undefined,
  index: number,
  isGraphPage: boolean,
  isBreakdownPlot = false,
  prefilter = '',
): string => {
  if (!plots) {
    return '';
  }

  const plot = plots[index];
  const search = new URLSearchParams();

  // this makes sure we have this property and it has a value
  const addIfDefined = (inObject: DashboardJSONType, name: string): void => {
    const value = inObject[name];
    if (value != null && value !== '') {
      search.set(name, value);
    }
  };

  let filter = extendFilter(graphConfig.filter, plot.filter);

  if (!isGraphPage) {
    filter = simplifyFilter(filter);
  } else if (prefilter) {
    filter = extendFilter(filter, prefilter);
  }

  if (graphConfig.breakdownFacet && isBreakdownPlot) {
    const breakdownValue = isNumber(plot.label) ? plot.label : `"${plot.label}"`;
    filter = addTermToFilter({ filter, field: graphConfig.breakdownFacet, op: '=', value: breakdownValue });
  }
  search.set('filter', filter);

  addIfDefined(plot, 'facet');

  if (isGraphPage) {
    search.set('mode', 'graph');
    search.set('label', plot.breakdownLabel ? (plot.breakdownLabel as string) : plot.label || 'Untitled');
    if (!plots[index].breakdownFacet) {
      addIfDefined(plot, 'color');
    }
    if (plots[index].plotStyle !== 'line') {
      search.set('graphStyle', plot.plotStyle as string);
    }
    addIfDefined(graphConfig, 'barWidth');
    addIfDefined(graphConfig, 'breakdownFacet');
    addIfDefined(graphConfig, 'lineSmoothing');
    addIfDefined(graphConfig, 'yScale');
    addIfDefined(graphConfig, 'numBars');
  }
  if (graphConfig.teamEmails) {
    search.set('teamEmails', graphConfig.teamEmails.join(','));
  }

  search.set('startTime', startTime);
  search.set('endTime', endTime);

  return `${K.ROUTE.EVENTS}?${search.toString()}`;
};

/**
 * Concatenate the dashboard filter url params together to create a Prefilter. This does not include the search
 * bar's prefilter.
 *
 * @param filters               The dashboardFilters url params
 * @param dashboardFilters     The current state of dashboard filters
 * @param searchFilter         The search filter
 */

export const concatenatePrefilters = (
  filters: string,
  dashboardFilters: FilterConfigType[],
  searchFilter: string,
): string => {
  const searchParams = new URLSearchParams(filters);

  const concatenatedDashboardFilters = dashboardFilters.reduce((prefilterString, filterConfig, i) => {
    const urlParamValue = searchParams.get(filterConfig.name);
    const escapedFacet = escapeFacetName(dashboardFilters[i].facet || '');

    // If a url param exists for a prefilter, then we prioritize concatenating that key value instead of the default value
    if (urlParamValue) {
      // if a facet and url param value exists, then concatenate accordingly.
      if (escapedFacet) {
        prefilterString += ` ${escapedFacet}=${quoteParamValue(urlParamValue)}`;

        // if no facet and url param value exists, then it is a "custom prefilter." We just append the urlParamValue which is
        // the selected value from the dropdown
      } else {
        prefilterString += ` ${urlParamValue}`;
      }

      // If no url param value exists, then we default to the default value if there is one. At the moment, default value
      // exists just for facet filters
    } else if (filterConfig.facet && filterConfig.defaultValue) {
      prefilterString += ` ${escapedFacet}=${quoteParamValue(filterConfig.defaultValue)}`;

      // If no url param value and no facet exists, then we have a "custom prefilter." We should apply the default filter,
      // which is the first value in labelsAndValues
    } else if (filterConfig.labelsAndValues.length) {
      const defaultCustomFilterValue = Object.values(filterConfig.labelsAndValues[0])[0];
      prefilterString += ` ${defaultCustomFilterValue}`;
    }

    return prefilterString;
  }, '');

  return (searchFilter + concatenatedDashboardFilters).trim();
};

/**
 * Retrieve a collection of facets from dashboard Filters and dashboard Parameters that are not included in facetInfo.
 * This set of facets are used to figure out which facet field inputs are missing dropdown values.
 *
 * @param dashboardFilters          An array of filter objects specified in the dashboard json
 * @param dashboardParameters       An array of parameter objects specified in the dashboard json
 * @param facetInfo                 The object containing all facet values
 */

export const getDashboardFacets = (
  dashboardFilters: FilterConfigType[] = [],
  dashboardParameters: ParameterConfigType[] = [],
  facetInfo: FacetsCompletionInfo,
): string[] =>
  [...dashboardParameters, ...dashboardFilters].reduce((facets: string[], paramOrFilter) => {
    if (paramOrFilter.facet && !facetInfo[paramOrFilter.facet]) {
      facets.push(paramOrFilter.facet);
    }

    return facets;
  }, []);

// Helper to extract out dashboard config fields and use them to construct search link
export const getExploreHref = (
  config: GraphConfigType,
  params: Array<DashboardParam>,
  startTime: string,
  endTime: string,
  prefilter = '',
) => {
  const graphStyleParams = {
    [K.URL_PARAM.GRAPH_STYLE]: config.graphStyle || '',
    [K.URL_PARAM.LINE_SMOOTHING]: config.lineSmoothing || '',
    [K.URL_PARAM.Y_SCALE]: config.yScale || '',
    [K.URL_PARAM.BAR_WIDTH]: config.barWidth || '',
    [K.URL_PARAM.NUM_BARS]: config.numBars || '',
    [K.URL_PARAM.MAX_PIE_SLICES]: config.maxPieSlices || '',
    [K.URL_PARAM.PIE_STYLE]: '',
    [K.URL_PARAM.TEAM_EMAILS]: config.teamEmails?.join(',') || '',
  };

  if (config?.graphStyle === K.PIE_CHART_TYPES.DONUT) {
    graphStyleParams[K.URL_PARAM.GRAPH_STYLE] = K.PIE_CHART_TYPES.PIE;
    graphStyleParams[K.URL_PARAM.PIE_STYLE] = K.PIE_CHART_TYPES.DONUT;
  }

  const filter = substituteParameters(params, config.query);

  return config.query
    ? buildUrl(K.ROUTE.POWER_QUERY, {
        [K.URL_PARAM.FILTER]: extendFilter(filter, prefilter),
        [K.URL_PARAM.START_TIME]: startTime,
        [K.URL_PARAM.END_TIME]: endTime,
        ...(config?.graphStyle !== K.PANEL_TYPE.NUMBER && graphStyleParams),
      } as UrlParams)
    : '';
};

export const computeHref = (
  dashboardName: string,
  config: GraphConfigType,
  graphIndex: number,
  params: Array<DashboardParam>,
  tabName?: string,
) => {
  if (config.query) {
    return buildUrl(K.ROUTE.POWER_QUERY, {
      [K.URL_PARAM.FILTER]: substituteParameters(params, config.query),
    } as UrlParams);
  }
  if (config.plots && config.graphStyle !== 'number') {
    return buildUrl(K.ROUTE.DASHBOARD, {
      [K.URL_PARAM.DASHBOARD_NAME]: dashboardName,
      [K.URL_PARAM.DASHBOARD_VIEW_GRAPH]: graphIndex.toString(),
      ...(tabName && tabName !== 'default' && { [K.URL_PARAM.DASHBOARD_ACTIVE_TAB]: tabName }),
    } as UrlParams);
  }
  return '';
};
